import { useControllableState } from '@radix-ui/react-use-controllable-state'
import clsx from 'clsx'
import { MinusIcon, PlusIcon } from 'lucide-react'
import { forwardRef, useEffect, useRef, useState } from 'react'

import { useValueSetter } from 'slab/hooks/useValueSetter'
import { mergeRefs } from 'utils/ref_utils'

interface CounterProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  defaultValue?: number | string;
  value?: number | string;
}

export const Counter = forwardRef<HTMLInputElement, CounterProps>(
  ({ defaultValue, disabled, value: valueProp, ...props }, forwardedRef) => {
    const [value, setValue] = useControllableState({
      defaultProp: defaultValue ?? '',
      prop: valueProp,
      // We use the `useControllableState` hook to track the input value in state
      // regardless of whether its controlled or uncontrolled by the owner so
      // we can enable/disable the increment/decrement buttons based on the
      // current value and the `min`/`max` props. We don't propagate state value
      // changes to the owner because the owner can track the input value via
      // the input's standard `onChange` event handler, so we're providing a
      // no-op function to this hook.
      onChange: () => {},
    })

    const [isFocused, setIsFocused] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const buttonClassNames =
      'size-8 bg-white hover:bg-gray-100 disabled:bg-gray-200 text-gray-800 disabled:text-gray-800/50 aria-disabled:text-gray-800/40 flex items-center justify-center shrink-0 bg-gradient-to-b from-black/5 to-50% disabled:bg-none aria-disabled:bg-none shadow-[0_1px_0_1px_rgba(255,255,255,.6)_inset] active:shadow-[0_1px_0_0_rgba(255,255,255,.6)_inset] disabled:shadow-[0_1px_0_1px_rgba(255,255,255,.6)_inset] transition-colors'

    // Tracks changes made to the input value directly via the DOM (for example,
    // by `react-hook-form`) so that we can properly disable the
    // increment/decrement buttons when that happens.
    useValueSetter(inputRef, (value) => {
      setValue(value)
    })

    useEffect(() => {
      if (isFocused && inputRef.current) {
        const input = inputRef.current

        function handleWheel (event: WheelEvent) {
          // The default behavior of the wheel event on number inputs is to
          // dramatically increment or decrement the value. This event handler
          // prevents that behavior.
          event.preventDefault()
        }

        // We can't use an `onWheel` callback on the input element because React
        // creates a passive event listener for the `wheel` event.
        input.addEventListener('wheel', handleWheel)

        return () => {
          input.removeEventListener('wheel', handleWheel)
        }
      }
    }, [isFocused])

    return (
      <div className='inline-flex items-center border border-gray-300 rounded-lg shadow-[0_1px_0_0] shadow-gray-300 outline outline-1 outline-transparent has-[:focus-visible]:outline-thatch-500 has-[:focus-visible]:border-thatch-500'>
        <button
          className={clsx(buttonClassNames, 'rounded-l-lg')}
          aria-label='Decrement'
          disabled={Number(value) <= Number(props.min) || disabled}
          onClick={() => {
            inputRef.current?.stepDown()
            inputRef.current?.dispatchEvent(
              new Event('input', { bubbles: true })
            )
          }}
          tabIndex={-1}
          type='button'
        >
          <MinusIcon size={16} />
        </button>
        <input
          {...props}
          className='h-8 w-10 border-y-0 border-x-px border-gray-300 focus:border-gray-300 focus:outline-none focus:ring-transparent bg-none body px-0.5 py-2 text-center text-gray-800 placeholder:text-gray-500 disabled:bg-gray-100'
          disabled={disabled}
          onFocus={(event) => {
            props.onFocus?.(event)
            setIsFocused(true)
          }}
          onBlur={(event) => {
            props.onBlur?.(event)
            setIsFocused(false)
          }}
          onChange={(event) => {
            setValue(event.target.value)
            props.onChange?.(event)
          }}
          ref={mergeRefs(inputRef, forwardedRef)}
          type='number'
          value={value}
        />
        <button
          className={clsx(buttonClassNames, 'rounded-r-lg')}
          aria-label='Increment'
          disabled={Number(value) >= Number(props.max) || disabled}
          onClick={() => {
            inputRef.current?.stepUp()
            inputRef.current?.dispatchEvent(
              new Event('input', { bubbles: true })
            )
          }}
          tabIndex={-1}
          type='button'
        >
          <PlusIcon size={16} />
        </button>
      </div>
    )
  }
)
